<script setup>
import { useAi } from '@/composables/useAi'
import { useLicenseStore } from '@/stores/license'
import { useAbility } from '@casl/vue'

// pages/ai/chat.vue — Module 22 (§9.7, §29.3). Chat AI + Draft SOP/CAPA +
// Analisis IKP dalam satu halaman bertab — blueprint's own Vue Page list
// cuma menyebut satu file untuk ini, bukan halaman terpisah per fitur.
definePage({
  meta: {
    layout: 'default',
    action: 'chat',
    subject: 'Ai',
  },
})

const licenseStore = useLicenseStore()
const ability = useAbility()
const { sending, draftSop, draftCapa, analyzeIncident } = useAi()

onMounted(() => {
  if (!licenseStore.status) licenseStore.fetchStatus()
})

const tab = ref('chat')

// ── Draft SOP ──────────────────────────────────────────────────────────
const sopForm = ref({ topic: '', notes: '' })
const sopDraft = ref(null)

const submitDraftSop = async () => {
  sopDraft.value = await draftSop(sopForm.value.topic, sopForm.value.notes || null)
}

// ── Draft CAPA ─────────────────────────────────────────────────────────
const capaForm = ref({ problem: '', root_cause: '' })
const capaDraft = ref(null)

const submitDraftCapa = async () => {
  capaDraft.value = await draftCapa(capaForm.value.problem, capaForm.value.root_cause || null)
}

// ── Analisis IKP ───────────────────────────────────────────────────────
const incidentId = ref(null)
const incidentAnalysis = ref(null)

const submitAnalyzeIncident = async () => {
  const result = await analyzeIncident(incidentId.value)

  incidentAnalysis.value = result.analysis
}
</script>

<template>
  <div>
    <h4 class="text-h4 font-weight-bold mb-1">
      AI Assistant
    </h4>
    <p class="text-body-2 text-medium-emphasis mb-4">
      Seluruh hasil AI adalah draf/saran — tetap perlu ditinjau dan disimpan manual (§29.1)
    </p>

    <VAlert
      v-if="licenseStore.status && !licenseStore.isFeatureEnabled('ai')"
      type="warning"
      variant="tonal"
    >
      Fitur AI Assistant tidak aktif untuk edisi lisensi ini.
    </VAlert>

    <VCard v-else>
      <VTabs v-model="tab">
        <VTab
          v-if="ability.can('chat', 'Ai')"
          value="chat"
        >
          Chat
        </VTab>
        <VTab
          v-if="ability.can('draft-sop', 'Ai')"
          value="draft-sop"
        >
          Draft SOP
        </VTab>
        <VTab
          v-if="ability.can('draft-capa', 'Ai')"
          value="draft-capa"
        >
          Draft CAPA
        </VTab>
        <VTab
          v-if="ability.can('analyze-incident', 'Ai')"
          value="analyze-incident"
        >
          Analisis IKP
        </VTab>
      </VTabs>

      <VDivider />

      <VWindow v-model="tab">
        <VWindowItem value="chat">
          <VCardText class="pa-6">
            <ChatWindow />
          </VCardText>
        </VWindowItem>

        <VWindowItem value="draft-sop">
          <VCardText class="pa-6">
            <VRow class="mb-2">
              <VCol cols="12">
                <VTextField
                  v-model="sopForm.topic"
                  label="Topik SOP"
                />
              </VCol>
              <VCol cols="12">
                <VTextarea
                  v-model="sopForm.notes"
                  label="Catatan Tambahan (opsional)"
                  rows="2"
                />
              </VCol>
            </VRow>
            <VBtn
              class="mb-4"
              :loading="sending"
              :disabled="!sopForm.topic"
              @click="submitDraftSop"
            >
              Buat Draf
            </VBtn>
            <DraftPreviewCard
              v-if="sopDraft"
              type="sop"
              :suggested-title="sopDraft.suggested_title"
              :draft-content="sopDraft.draft_content"
              :note="sopDraft.note"
            />
          </VCardText>
        </VWindowItem>

        <VWindowItem value="draft-capa">
          <VCardText class="pa-6">
            <VRow class="mb-2">
              <VCol cols="12">
                <VTextField
                  v-model="capaForm.problem"
                  label="Masalah"
                />
              </VCol>
              <VCol cols="12">
                <VTextarea
                  v-model="capaForm.root_cause"
                  label="Akar Masalah (opsional, bila sudah diketahui)"
                  rows="2"
                />
              </VCol>
            </VRow>
            <VBtn
              class="mb-4"
              :loading="sending"
              :disabled="!capaForm.problem"
              @click="submitDraftCapa"
            >
              Buat Draf
            </VBtn>
            <DraftPreviewCard
              v-if="capaDraft"
              type="capa"
              :suggested-title="capaDraft.suggested_title"
              :draft-content="capaDraft.draft_content"
              :note="capaDraft.note"
            />
          </VCardText>
        </VWindowItem>

        <VWindowItem value="analyze-incident">
          <VCardText class="pa-6">
            <VRow class="mb-2">
              <VCol
                cols="12"
                md="6"
              >
                <VTextField
                  v-model="incidentId"
                  type="number"
                  label="ID Insiden (IKP)"
                  hint="Lihat daftar di menu Peningkatan Mutu → IKP"
                  persistent-hint
                />
              </VCol>
            </VRow>
            <VBtn
              class="mb-4 mt-2"
              :loading="sending"
              :disabled="!incidentId"
              @click="submitAnalyzeIncident"
            >
              Analisis
            </VBtn>
            <VCard
              v-if="incidentAnalysis"
              variant="tonal"
              color="info"
            >
              <VCardText>
                <p style="white-space: pre-wrap;">
                  {{ incidentAnalysis }}
                </p>
                <VAlert
                  type="warning"
                  variant="tonal"
                  density="compact"
                  class="mt-3"
                >
                  Ini hanya saran AI — bukan keputusan final, wajib diverifikasi Tim Mutu (§29.3).
                </VAlert>
              </VCardText>
            </VCard>
          </VCardText>
        </VWindowItem>
      </VWindow>
    </VCard>
  </div>
</template>
